import { memo } from 'react';
import { Handle, Position, type NodeProps, useReactFlow } from 'reactflow';
import { EthernetPort, X } from 'lucide-react';
import { useHoverStore } from '../../../utils/store';


interface PortMapping {
    id: number;
    port_index: number;
    port_name: string;
    port_number: string;
    port_status: string;
    mac_address: string;
    connected_to: any;
}


interface SwitchNodeData {
    switchName?: string;
    assetName?: string;
    portMappings: PortMapping[];
    onPortClick: (port: PortMapping) => void;
    readOnly?: boolean;
}


const SwitchNode = memo(({ id, data }: NodeProps<SwitchNodeData>) => {
    const displayName = data.switchName || data.assetName || 'Switch';
    const { portMappings, onPortClick } = data;
    const { deleteElements } = useReactFlow();


    const setPortHovered = useHoverStore((state) => state.setPortHovered);


    const portColumns: PortMapping[][] = [];
    if (portMappings && Array.isArray(portMappings)) {
        for (let i = 0; i < portMappings.length; i += 2) {
            portColumns.push(portMappings.slice(i, i + 2));
        }
    }


    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        deleteElements({ nodes: [{ id }] });
    };


    return (
        <div className="relative group shadow-2xl rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 w-fit cursor-pointer">
            {/* Delete Button */}
            {!data?.readOnly && (
                <button
                    onClick={handleDelete}
                    className="absolute -top-3 -right-3 z-50 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg hover:bg-red-600 hover:scale-110"
                >
                    <X size={14} strokeWidth={3} />
                </button>
            )}


            {/* Header */}
            <div className="bg-gray-50 dark:bg-gray-900 px-4 py-2 rounded-t-lg border-b border-gray-200 dark:border-gray-700 flex items-center justify-between min-w-[150px]">
                <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    <span className="text-gray-800 dark:text-gray-200 font-bold text-xs uppercase tracking-wider truncate max-w-[150px]">
                        {displayName}
                    </span>
                </div>
            </div>


            {/* Ports Container */}
            <div className="p-2 rounded-b-lg flex gap-1 justify-center bg-gray-50/50 dark:bg-transparent">
                {portColumns.map((column, colIndex) => (
                    <div key={colIndex} className="flex flex-col gap-2">
                        {column.map((port, rowIndex) => {
                            const isTopRow = rowIndex === 0;
                            const isConnected = port.connected_to !== null;
                            const isAssetOffline = port.connected_to?.status === 'offline' || port.connected_to?.status === 'in_active';
                            const isUp = port.port_status === 'up';
                            const isDown = port.port_status === 'down';


                            const connectedAssetId = port.connected_to?.id || port.connected_to?.asset_id;
                            const targetAssetNodeId = connectedAssetId ? `detail-asset-${connectedAssetId}` : null;
                            const sourceHandleId = `port-${port.id}`;


                            return (
                                <div
                                    key={port.id}
                                    className="relative group/port"
                                    onMouseEnter={() => setPortHovered(sourceHandleId, targetAssetNodeId)}
                                    onMouseLeave={() => setPortHovered(null, null)}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onPortClick(port);
                                    }}
                                >
                                    {/* Port Number Tooltip */}
                                    <div className={`absolute left-1/2 -translate-x-1/2 z-50 opacity-0 group-hover/port:opacity-100 pointer-events-none transition-all duration-200 ${isTopRow ? '-top-10' : '-bottom-10'
                                        }`}>
                                        <div className="bg-white dark:bg-gray-900 flex flex-col items-start gap-0.5 text-gray-900 dark:text-white text-[10px] font-bold px-2 py-1 rounded border border-gray-200 dark:border-gray-700 shadow-2xl whitespace-nowrap">
                                            <div className="flex items-center gap-1.5">
                                                <span>{port.port_number || port.port_name}</span>
                                                <span className={`${isUp ? 'text-green-500 dark:text-green-400' : isDown ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>({port.port_status})</span>
                                            </div>
                                            {(port as any).type && (
                                                <div className="text-[9px] text-blue-600 dark:text-blue-400 font-normal">Type: {(port as any).type}</div>
                                            )}
                                            {(port as any).ip_address && (port as any).ip_address !== '0.0.0.0' && (
                                                <div className="text-[9px] text-cyan-600 dark:text-cyan-300 font-mono font-normal">IP: {(port as any).ip_address}</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className={`w-8 h-8 flex items-center justify-center rounded border transition-all duration-200 relative z-10 ${isConnected ? 'bg-blue-50 dark:bg-gray-700 border-blue-500/50 shadow-inner' : 'bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-700 opacity-60'
                                        } hover:bg-gray-200 dark:hover:bg-gray-600`}>
                                        <div className={`absolute top-0.5 right-0.5 w-1 h-1 rounded-full ${isUp ? 'bg-green-500' : isDown ? 'bg-red-500' : 'bg-gray-400 dark:bg-gray-600'}`}></div>
                                        <EthernetPort
                                            size={18}
                                            className={`${!isTopRow ? 'rotate-180' : ''} ${isAssetOffline
                                                ? 'text-red-500 dark:text-red-400 animate-pulse'
                                                : isUp
                                                    ? 'text-green-600 dark:text-green-400'
                                                    : isDown
                                                        ? 'text-red-500 dark:text-red-400'
                                                        : isConnected
                                                            ? 'text-green-600 dark:text-green-400'
                                                            : 'text-gray-400 dark:text-gray-600'
                                                }`}
                                        />
                                    </div>


                                    <Handle
                                        type="source"
                                        position={isTopRow ? Position.Top : Position.Bottom}
                                        id={sourceHandleId}
                                        className={`!w-2 !h-2 !border-2 !rounded-full transition-all z-30 ${isConnected ? '!bg-gray-400 dark:!bg-gray-600 !border-gray-300 dark:!border-gray-500' : '!bg-blue-500 !border-white dark:!border-gray-900 hover:!w-3 hover:!h-3'
                                            }`}
                                        style={{ top: isTopRow ? -5 : 'auto', bottom: isTopRow ? 'auto' : -5, left: '50%', transform: 'translateX(-50%)' }}
                                    />


                                    <Handle
                                        type="target"
                                        position={isTopRow ? Position.Top : Position.Bottom}
                                        id={`port-in-${port.id}`}
                                        className="opacity-0 z-20"
                                        style={{ 
                                            position: 'absolute',
                                            top: -2, 
                                            left: -2, 
                                            width: 'calc(100% + 4px)', 
                                            height: 'calc(100% + 4px)', 
                                            transform: 'none',
                                            cursor: 'copy'
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
});


export default SwitchNode;
